define('datakit/model',[
  'backbone',
  'underscore',

  './model.registry',
  './collection',
  './util/url',
  './util/getobject'
], function(
  Backbone,
  _,

  ModelRegistry,
  DKCollection,
  url,
  getobject
) {

  var Model = Backbone.Model.extend({

    // many times you need to create an entity using a specificly formatted url
    // once the entity is created it can revert to using standard CRUD apis
    urlTemplate: null,

    virtual: {},

    childModels: {},

    constructor: function(attrs, options) {
      var childModels = this.childModels,
          virtual     = this.virtual;

      this.childModels = {};
      this.virtual = {};

      _.each(virtual, function(def, key) {
        if (_.isFunction(def)) {
          def = { get: def };
        }
        if (!_.isEmpty(def.deps)) {
          var events = _.map(def.deps, function(attr) {
            // make sure we don't get into an event loop
            if (attr == key) { return ''; }
            return 'change:' + attr;
          }).join(' ');
          this.on(events, function() {
            this.trigger('change:' + key);
          }, this);
        }
        this.virtual[key] = def;
      }, this);

      _.each(childModels, function(type, key) {
        var ctor;
        if (_.isString(type)) {
          ctor = ModelRegistry.get(type);
          if (!ctor) {
            throw new Error("The model of type \"" + type + "\" for key \"" + key + "\" does not exist.  You may need to include it.");
          }
        } else if (_.isFunction(type)) {
          ctor = type;
        } else {
          throw new Error("Unknown Model type for key \"" + key + "\"");
        }
        this.childModels[key] = ctor;
      }, this);

      return Backbone.Model.apply(this, arguments);
    },

    initialize: function(attrs, options) {
      options = options || {};
      Backbone.Model.prototype.initialize.apply(this, arguments);

      if (options.url) {
        this.urlTemplate = options.url;
      }

      this.fetched = false;
      this.once('sync', function() {
        this.fetched = true;
      }, this)
    },

    createCompositeKey: function(key) {
      var genKey = "";

      for (var i = 0; i < this.compositeKey.length; i++) {
        if (i > 0) { genKey += "_"; };
        if (!key[this.compositeKey[i]]) { return undefined; }
        genKey += key[this.compositeKey[i]];
      }
      return genKey;
    },

    clone: function() {
      var childModelKeys = _.keys(this.childModels),
          attrs          = _.clone(this.attributes);

      // perform a deep clone on nested child models
      for (var i = 0, key, value; i < childModelKeys.length; i++) {
        key = childModelKeys[i];
        value = this.get(key);
        if (value instanceof Backbone.Model) {
          attrs[key] = value.clone();
        } else if (value instanceof Backbone.Collection) {
          attrs[key] = value.map(function(model) {
            return model.clone();
          });
        }
      };
      return new this.constructor(attrs);
    },

    get: function(attr) {
      // Call the getter if available
      var virtual    = this.virtual[attr];
      if (virtual && _.isFunction(virtual.get)) {
        return virtual.get.call(this);
      } else if (String(attr).indexOf('.') > -1) {
        return getobject.get(this, attr, false);
      }
      return Backbone.Model.prototype.get.call(this, attr);
    },

    set: function(key, value, options) {
      var attrs, attr, virtual, isChildModel, model, childOptions, isArray;

      // Normalize the key-value into an object
      if (_.isObject(key) || key == null) {
        attrs = _.clone(key);
        options = value;
      } else {
        attrs = {};
        attrs[key] = value;
      }
      options = options || {};

      // this model uses a composite key and there is a key field set to a value.
      // Note that we only need to do this processing if the composite key is in object form.
      // if it has been converted to a string then we can assume that the composite key properties
      // have already been set as the model's attributes
      if (this.compositeKey && _.isObject(attrs[this.idAttribute || 'id'])) {
        // save the provided key
        var key = attrs[this.idAttribute || 'id'];

        // generate the composite key.  this involve turning the composite key (object) into a string
        var genKey = attrs[this.idAttribute || 'id'] = this.createCompositeKey(key);

        // if we generated a composite key, store the components of the keys as attributes on the model.
        if (genKey) {
          for(var i = 0; i < this.compositeKey.length; i++) {
            attrs[this.compositeKey[i]] = key[this.compositeKey[i]];
          }
        }
      }

      for (attr in attrs) {
        value = attrs[attr];
        virtual = this.virtual[attr];
        // check to see if
        isChildModel = !!this.childModels[attr];

        if (isChildModel) {
          model = this.get(attr);
          // if we have an object for the childModel then update the childModel properties
          if (_.isObject(value) && !(value instanceof Backbone.Model) && !(value instanceof Backbone.Collection)) {
            isArray = _.isArray(value);
            childOptions = _.extend({}, options, { collection: isArray, defaults: value })
            if (isArray && !(model instanceof Backbone.Collection)) {
              this._setupChildModel(attr, childOptions);
            } else if (!(model instanceof Backbone.Model) && !(model instanceof Backbone.Collection)) {
              this._setupChildModel(attr, childOptions);
            } else if (!isArray && value[model.idAttribute] && value[model.idAttribute] != model.id) {
              // value is not an array
              // value has an id attribute
              // and that id DOES NOT match the current model's id
              // then we need to create an entirely new model
              this.stopListening(model);
              this._setupChildModel(attr, childOptions);
            } else {
              model.set(value);
            }
            // update the attributes on the child model
            // delete it from being updating
            // via the normal set method
            delete attrs[attr];
          } else if ((model instanceof Backbone.Model) || (model instanceof Backbone.Collection)) {
            // make sure we stop listening to the previous value
            // if we are setting a new one up
            this.stopListening(model);
            // if we are setting a new model/collection value
            // then go ahead and listen to that one!
            if ((value instanceof Backbone.Model) || (value instanceof Backbone.Collection)) {
              this._setupChildModelEvents(attr, value);
            }
          }
        } else if (virtual) {
          // Virtual properties are read only
          delete attrs[attr];
        } else if (String(attr).indexOf('.') > -1) {
          getobject.set(this, attr, value);
          delete attrs[attr];
        }
      }

      return Backbone.Model.prototype.set.call(this, attrs, options);
    },

    _setupChildModel: function(key, options) {
      var ChildModel = this.childModels[key],
          model      = null;

      if (options.collection) {
        model = new DKCollection(options.defaults, { model: ChildModel });
      } else {
        model = new ChildModel(options.defaults);
      }
      // keep a reference to the parent
      // so the child can trigger events
      this._setupChildModelEvents(key, model);
      return Backbone.Model.prototype.set.call(this, key, model, options);
    },

    _setupChildModelEvents: function(key, model) {
      // listen for changes on the model that we are setting
      // so that we can know when it changes
      model._parent = this;
      model._parentKey = key;
      this.listenTo(model, 'change', function(m, options) {
        this.trigger('change:' + key, this, model, options);
      });
    },

    params: function() {
      if (!this._params) {
        this._params = new Backbone.Model();
      }
      this._params.clear();
      this._params.set.apply(this._params, arguments);
      return this;
    },

    url: function() {
      if (this.urlTemplate && this.isNew()) {
        return url.template(this.urlTemplate, this._params, this);
      } else if (this.compositeKey) {
        var base =
          _.result(this, 'urlRoot') ||
          _.result(this.collection, 'url') ||
          urlError();
        if (this.isNew()) return base;
        var id = "";

        for (var i = 0; i < this.compositeKey.length; i++) {
          if (i > 0) { id += "/"; };

          var keyVal = this.get(this.compositeKey[i]);

          if (!keyVal) { return null; }
          id += encodeURIComponent(keyVal);
        }

        return base.replace(/([^\/])$/, '$1/') + id;
      } else {
        return Backbone.Model.prototype.url.apply(this, arguments);
      }
    },

    request: function(urlPath, options) {
      var model = this,
          success,
          error;

      if (_.isObject(urlPath)) {
        options = urlPath;
        urlPath = null;
      } else {
        urlPath = url.template(urlPath, this);
      }

      options = _.extend({ type: 'GET', url: urlPath }, options);

      success = options.success;
      error   = options.error;
      options.success = function(resp) {
        var evName = options.successEvent || 'request:success';
        if (success) {
          success.call(options.context, model, resp, options);
        } else {
          model.parse(resp);
        }
        // we cannot always trigger a sync here because the operation
        // may not sync the data with the server
        if (options.triggerSync) {
          evName = 'sync';
        }
        model.trigger(evName, model, resp, options);
      };
      options.error = function(resp) {
        if (error) { error.call(options.context, model, resp, options); }
        model.trigger('error', model, resp, options);
      }

      return this.sync(options.syncMethod || null, this, options);
    },

    collectionParse: function(resp) {
      return resp;
    },

    sync: function(method, model, options) {
      var error      = null,
          beforeSend = null,
          xhr        = null,
          success    = null;

      options = _.extend({}, options);
      beforeSend = options.beforeSend;
      options.beforeSend = function() {
        xhr = arguments[0];
        if (beforeSend) return beforeSend.apply(this, arguments);
      };

      success = options.success;
      error = options.error;
      options.success = function(resp) {
        // wrap the success handler in a try/catch block
        // so that if any errors happen, it will go down the error
        // chain which will trigger an error event
        try {
          // by calling success we let backbone deal with parsing
          // the model. if there is no success we still need to verify
          // that the response is valid
          if (method == 'delete') {
            // if we are doing a delete then we always
            // want to call parse because the native success call
            // does not call parse
            model.parse(resp);
            if (success) { success(resp); }
          } else {
            success ? success(resp) : model.parse(resp);
          }
        } catch (err) {
          if (error) { error.call(options.context, xhr, 'error', err); }
          throw err;
        }
      }


      return Backbone.sync.call(this, method, model, options);
    },

    toJSON: function(options) {
      var data = Backbone.Model.prototype.toJSON.apply(this, arguments);

      if (this.compositeKey && data[this.idAttribute || 'id']) {
        var key = {};
        for (var i = 0; i < this.compositeKey.length; i++) {
          if (!data[this.compositeKey[i]]) {
            key = null;
            break; // out of the foor loop
          }
          key[this.compositeKey[i]] = data[this.compositeKey[i]];
        }
        data[this.idAttribute || 'id'] = key;
      }

      _.each(this.childModels, function(ctor, key) {
        var model = this.get(key);
        if ((model instanceof Backbone.Model) || (model instanceof Backbone.Collection)) {
          data[key] = this.serializeChildModel(model, options);
        }
      }, this);

      return data;
    },

    serializeChildModel: function(model, options) {
      if (model instanceof Backbone.Model) {
        var keys = _.keys(model.childModels);
        // we want the idAttribute and any nested models
        return _.pick(model.toJSON(), [model.idAttribute].concat(keys));
      } else if (model instanceof Backbone.Collection) {

        return model.map(function(m) {
          return this.serializeChildModel(m, options);
        }, this);
      }
    }

  },
  {

    Collection: DKCollection,

    createCollection: function(urlPath, options) {
      if (_.isObject(urlPath)) {
        options = urlPath;
        urlPath = null;
      }
      
      return new this.Collection([], _.extend({ model: this, url: urlPath, parseHandler: this.prototype.collectionParse }, options));
    },

    all: function(options) {
      return this.createCollection(this.prototype.urlRoot, options);
    },

    createKeyPathVirtual: function(keypath, deps) {
      return {
        deps: deps,
        get: function() {
          return getobject.get(this, keypath);
        }
      };
    },

    register: function(name, Ctor) {
      ModelRegistry.register(name, Ctor);
      return this;
    },

    get: function(name) {
      return ModelRegistry.get(name);
    }

  });

  return Model;
});

